import React, { useState, useRef, useCallback } from "react";
import { Spin, Upload, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { UploadRequestOption as RcCustomRequestOptions } from "rc-upload/lib/interface";
import type { UploadFile } from "antd/es/upload/interface";
import { getCategoryPhoto, uploadCategoryPhoto } from "helpers/backend_helper";
import { useRequest } from "ahooks";

interface Props {
  categoryId: string;
  localCountry: string;
  size?: number;
}

const addTimestampToUrl = (url: string) => {
  if (!url) return url;
  const ts = `_ts=${Date.now()}`;
  return url.includes("?") ? `${url}&${ts}` : `${url}?${ts}`;
};

export const UploadPicComponent: React.FC<Props> = ({ categoryId, localCountry, size = 120 }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const lastObjectUrlRef = useRef<string | null>(null);

  const revokeLastObjectUrl = useCallback(() => {
    if (lastObjectUrlRef.current) {
      try {
        URL.revokeObjectURL(lastObjectUrlRef.current);
      } catch {
        // ignore
      }
      lastObjectUrlRef.current = null;
    }
  }, []);

  const customUpload = async (options: RcCustomRequestOptions) => {
    const { file, onSuccess, onError, onProgress } = options;

    const uid = (file as any).uid ?? `${Date.now()}`;
    setFileList([
      {
        uid,
        name: (file as any).name ?? "file",
        status: "uploading",
        percent: 0,
        originFileObj: (file as any).originFileObj ?? file,
      },
    ]);
    revokeLastObjectUrl();
    setPreviewUrl(null);

    try {
      const res = await uploadCategoryPhoto(categoryId, file as unknown as UploadFile, localCountry, (progress) => {
        setFileList((prev) => prev.map((f) => (f.uid === uid ? { ...f, percent: progress.percent } : f)));
        onProgress?.({ percent: progress.percent });
      });

      onSuccess?.(res);

      let urlFromServer: string | undefined;
      if (res && typeof res === "object") {
        urlFromServer = (res as any).url ?? (res as any).data?.url;
      } else if (typeof res === "string") {
        urlFromServer = res;
      }

      let finalUrl: string;
      if (urlFromServer) {
        finalUrl = addTimestampToUrl(urlFromServer);
      } else {
        const origin = (file as any).originFileObj ?? file;
        finalUrl = URL.createObjectURL(origin as Blob);
        lastObjectUrlRef.current = finalUrl;
      }

      const doneFile: UploadFile = {
        uid,
        name: (file as any).name ?? "file",
        status: "done",
        url: finalUrl,
      };
      setFileList([doneFile]);
      console.log();
      setPreviewUrl(URL.createObjectURL(file as Blob));

      message.success("Фото категории успешно загружено");
    } catch (err) {
      setFileList([]);
      setPreviewUrl(null);
      onError?.(err as Error);
      message.error("Ошибка при загрузке фото");
    }
  };

  const handleRemove = () => {
    revokeLastObjectUrl();
    setFileList([]);
    setPreviewUrl(null);
  };

  const uploadProps = {
    customRequest: customUpload as any,
    fileList,
    onChange: ({ fileList: newList }: { fileList: UploadFile[] }) => {
      setFileList(newList);
      if (newList.length === 0) {
        revokeLastObjectUrl();
        setPreviewUrl(null);
      } else if (newList[0].url) {
        console.log(newList[0].url);
        setPreviewUrl(newList[0].url as string);
      }
    },
    onRemove: handleRemove,
    showUploadList: false,
    accept: "image/*",
  };

  const containerStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: "50%",
    border: "1px dashed #d9d9d9",
    overflow: "hidden",
    position: "relative",
    cursor: "pointer",
    display: "inline-block",
  };

  const [prevImage, setPrevImage] = useState(null);

  const { loading: loadingPrevPhoto } = useRequest(() => getCategoryPhoto(categoryId, localCountry), {
    ready: !!categoryId && !!localCountry,
    refreshDeps: [],
    onSuccess: (blob: any) => {
      setPrevImage(URL.createObjectURL(blob.data));
    },
  });

  return (
    <>
      <Upload {...uploadProps}>
        <div className="avatar-container" style={containerStyle}>
          {previewUrl || prevImage ? (
            <>
              <img
                key={previewUrl ?? prevImage}
                src={!previewUrl ? prevImage : previewUrl}
                alt="avatar"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              <div className="upload-overlay">
                <PlusOutlined style={{ fontSize: Math.round(size * 0.22), color: "#fff" }} />
              </div>
            </>
          ) : loadingPrevPhoto ? (
            <Spin style={{ top: "50%", left: "50%", position: "absolute", transform: "translate(-50%, -50%)" }} />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
                fontSize: Math.round(size * 0.25),
              }}
            >
              <PlusOutlined />
            </div>
          )}
        </div>
      </Upload>

      <style>{`
        .avatar-container .upload-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.18s;
        }
        .avatar-container:hover .upload-overlay {
          opacity: 1;
        }
      `}</style>
    </>
  );
};
